<template>
  <section class="admin-content" id="contact-search">
    <Navbar />
    <main class="admin-main">

      <!-- ── Page Header ── -->
      <div class="page-header">
        <div class="container">
          <div class="d-flex flex-wrap align-items-center text-white" style="padding: 32px 0; gap: 18px;">
            <img :src="client.image_url || '/assets/img/ohis_logo.png'" class="header-avatar patient-avatar" alt="Patient photo"
              onerror="this.onerror=null; this.src='/assets/img/ohis_logo.png'" />
            <div class="flex-grow-1" style="min-width: 220px;">
              <small class="text-uppercase font-weight-bold" style="opacity: .75; letter-spacing: .08em;">
                Patient utilization · {{ isDependant ? 'Dependant' : 'Principal' }}
              </small>
              <h3 class="page-title text-white mb-2">{{ client.full_name || '…' }}</h3>
              <div class="d-flex flex-wrap" style="gap: 8px;">
                <span v-if="client.id_card_number" class="header-id-badge">
                  <i class="fe fe-credit-card mr-1"></i> {{ client.id_card_number }}
                </span>
                <span v-if="programme" class="header-id-badge">
                  <i class="fe fe-layers mr-1"></i> {{ programme }}
                </span>
                <span v-if="client.expiry" class="header-status"
                  :class="client.expiry === 'active' ? 'header-status--active' : 'header-status--inactive'">
                  <i class="fe mr-1" :class="client.expiry === 'active' ? 'fe-check-circle' : 'fe-alert-circle'"></i>
                  Cover {{ client.expiry === 'active' ? 'active' : 'expired' }}
                </span>
              </div>
            </div>
            <router-link v-if="profileLink" :to="profileLink" class="btn btn-light btn-sm font-weight-bold">
              <i class="fe fe-user mr-1"></i> Full profile
            </router-link>
          </div>
        </div>
      </div>

      <div class="container mt-n3 pb-5">

        <!-- ── KPI tiles ── -->
        <div class="row stat-tiles">
          <div class="col-lg-3 col-sm-6 mb-4" v-for="kpi in kpis" :key="kpi.label">
            <div class="card border-0 shadow-sm h-100">
              <div class="card-body py-3 px-4 d-flex align-items-center" style="gap: 14px;">
                <div class="avatar avatar-md flex-shrink-0">
                  <div class="avatar-title rounded-circle" :class="kpi.tone"><i :class="kpi.icon"></i></div>
                </div>
                <div style="min-width: 0;">
                  <p class="mb-0 text-muted" style="font-size: .72rem; text-transform: uppercase; letter-spacing: .04em;">{{ kpi.label }}</p>
                  <h4 class="mb-0 font-weight-bold text-dark text-truncate">{{ kpi.value }}</h4>
                  <small v-if="kpi.hint" class="text-muted">{{ kpi.hint }}</small>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- ── Cost of care ── -->
        <PatientCosts v-if="$route.params.id" :type="isDependant ? $route.params.type : 'client'" :id="$route.params.id" @loaded="costs = $event" />

        <div class="row">
          <!-- ── Profile ── -->
          <div class="col-lg-5 mb-4">
            <div class="card shadow-sm form-card h-100">
              <div class="form-card-header">
                <div class="section-number"><i class="fe fe-user" style="font-size: 12px;"></i></div>
                <h6 class="mb-0 font-weight-bold text-white">Patient details</h6>
              </div>
              <ul class="p-0 m-0">
                <li class="info-row" v-for="row in profileRows" :key="row.label">
                  <i class="info-icon" :class="row.icon"></i>
                  <div style="min-width: 0;">
                    <p class="info-label">{{ row.label }}</p>
                    <router-link v-if="row.to" :to="row.to" class="info-value d-block">{{ row.value }}</router-link>
                    <p v-else class="info-value">{{ row.value }}</p>
                  </div>
                </li>
              </ul>
            </div>
          </div>

          <!-- ── Authorization codes ── -->
          <div class="col-lg-7 mb-4">
            <div class="card shadow-sm form-card h-100">
              <div class="form-card-header">
                <div class="section-number"><i class="fe fe-send" style="font-size: 12px;"></i></div>
                <h6 class="mb-0 font-weight-bold text-white">Authorization codes</h6>
                <span class="badge badge-light ml-auto" style="border-radius: 20px;">{{ referrals.meta.total || 0 }}</span>
              </div>
              <div class="card-body px-4 py-3 code-list">
                <div v-for="code in referrals.data" :key="code.id" class="code-row">
                  <span class="code-dot" :class="'code-dot--' + codeTone(code.status)">
                    <i :class="codeIcon(code.status)"></i>
                  </span>
                  <div class="flex-grow-1" style="min-width: 0;">
                    <router-link :to="`/authorization-code/${code.id}`" class="font-weight-bold d-block text-truncate">
                      {{ (code.encounter && code.encounter.healthrecord && code.encounter.healthrecord.encounter_id) || ('Request #' + code.id) }}
                    </router-link>
                    <small class="text-muted d-block text-truncate">
                      <span v-if="code.provider">{{ code.provider.agency_name }}</span>
                      <span v-if="code.recipientfacility"> → {{ code.recipientfacility.agency_name }}</span>
                    </small>
                  </div>
                  <div class="text-right flex-shrink-0">
                    <span class="pill" :class="'pill--' + codeTone(code.status)">{{ code.status || 'pending' }}</span>
                    <small class="d-block text-muted mt-1">{{ code.date_requested | moment("D MMM YYYY") }}</small>
                    <code v-if="code.code_created" class="small">{{ code.code_created }}</code>
                  </div>
                </div>
                <div v-if="!referrals.data.length" class="empty-state">
                  <i class="fe fe-inbox"></i>
                  <p>No authorization codes</p>
                </div>
                <div v-if="client.created_at" class="code-row code-row--muted">
                  <span class="code-dot code-dot--info"><i class="fe fe-user-plus"></i></span>
                  <div class="flex-grow-1 font-weight-bold">Enrolled</div>
                  <small class="text-muted">{{ client.created_at | moment("D MMM YYYY") }}</small>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- ── Utilization history: encounters and claims ── -->
        <div class="card shadow-sm table-card">
          <div class="form-card-header flex-wrap">
            <div class="section-number"><i class="fe fe-clock" style="font-size: 12px;"></i></div>
            <h6 class="mb-0 font-weight-bold text-white flex-grow-1">Utilization history</h6>
            <div class="history-tabs" role="tablist">
              <button type="button" role="tab" :aria-selected="tab === 'encounters'" class="history-tab" :class="{ 'history-tab--on': tab === 'encounters' }" @click="tab = 'encounters'">
                <i class="fe fe-activity mr-1"></i>Encounters <span class="history-count">{{ records.meta.total || 0 }}</span>
              </button>
              <button type="button" role="tab" :aria-selected="tab === 'claims'" class="history-tab" :class="{ 'history-tab--on': tab === 'claims' }" @click="tab = 'claims'">
                <i class="fe fe-file-text mr-1"></i>Claims <span class="history-count">{{ claimsTotal }}</span>
              </button>
            </div>
          </div>
          <div class="table-responsive" v-show="tab === 'encounters'">
            <table class="table align-td-middle mb-0">
              <thead class="table-head">
                <tr>
                  <th class="table-th">Encounter ID</th>
                  <th class="table-th">Health facility</th>
                  <th class="table-th">Diagnosis</th>
                  <th class="table-th">Date</th>
                  <th class="table-th"></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="record in records.data" :key="record.id" class="table-row">
                  <td class="px-4 font-weight-bold">{{ record.healthrecord && record.healthrecord.encounter_id }}</td>
                  <td>{{ record.provider && record.provider.agency_name }}</td>
                  <td>{{ record.diagnosis && record.diagnosis.name }}</td>
                  <td class="text-nowrap">{{ record.created_at | moment("D MMM YYYY") }}</td>
                  <td class="text-right pr-4">
                    <router-link :to="`/encounter/${record.id}`" class="btn btn-sm btn-outline-primary">
                      <i class="fe fe-eye"></i>
                    </router-link>
                  </td>
                </tr>
                <tr v-if="!records.data.length">
                  <td colspan="5" class="empty-state"><i class="fe fe-inbox"></i><p>No encounters</p></td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="table-responsive" v-show="tab === 'claims'">
            <table class="table align-td-middle mb-0">
              <thead class="table-head">
                <tr>
                  <th class="table-th">#</th>
                  <th class="table-th">Health facility</th>
                  <th class="table-th">Diagnosis</th>
                  <th class="table-th text-right">Requested</th>
                  <th class="table-th text-right">Approved</th>
                  <th class="table-th">Status</th>
                  <th class="table-th">Date</th>
                  <th class="table-th"></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(claim, index) in claims.data" :key="claim.id" class="table-row">
                  <td class="px-4 text-muted small">{{ index + 1 }}</td>
                  <td>{{ claim.provider && claim.provider.agency_name }}</td>
                  <td>{{ claim.diagnosis && claim.diagnosis.name }}</td>
                  <td class="text-right text-nowrap">₦{{ claim.requested_total_cost | numeral(0, 0) }}</td>
                  <td class="text-right text-nowrap font-weight-bold">₦{{ claim.approved_total_cost | numeral(0, 0) }}</td>
                  <td>
                    <span class="pill" :class="'pill--' + claimStatus(claim).variant">
                      <i class="fe mr-1" :class="claimStatus(claim).icon"></i>{{ claimStatus(claim).label }}
                    </span>
                  </td>
                  <td class="text-nowrap">{{ claim.created_at | moment("D MMM YYYY") }}</td>
                  <td class="text-right pr-4">
                    <router-link :to="`/claim/${claim.id}`" class="btn btn-sm btn-outline-primary">
                      <i class="fe fe-eye"></i>
                    </router-link>
                  </td>
                </tr>
                <tr v-if="!claims.data.length">
                  <td colspan="8" class="empty-state"><i class="fe fe-inbox"></i><p>No claims</p></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div class="vld-parent">
          <loading :active.sync="isLoading" loader="dots" :can-cancel="true" :is-full-page="fullPage"></loading>
        </div>
      </div>
    </main>
  </section>
</template>

<script>
import Navbar from "@/views/Navbar.vue";
import Loading from "vue-loading-overlay";
import "vue-loading-overlay/dist/vue-loading.css";
import moment from "moment";
import { getClaimStatus } from "@/utils/claimStatus";
import PatientCosts from "./components/PatientCosts.vue";

let agency = JSON.parse(localStorage.getItem("agency")) || { id: 407465 }; // RIVCHPP

const CLAIMS_PER_PAGE = 100;

export default {
  components: { Navbar, Loading, PatientCosts },
  data() {
    return {
      user: null,
      agency,
      utilization: null, // only the dependant endpoint returns it
      costs: null, // from PatientCosts (GET patients/{type}/{id}/costs)
      tab: "encounters",
      records: { data: [], meta: { total: 0 } },
      claims: { data: [], total: 0 },
      referrals: { data: [], meta: { total: 0 } },
      client: "",
      healthFacility: "",
      tpa: "",
      loading: 0,
      fullPage: true,
    };
  },
  computed: {
    // The route type is the enrollee's users.type: "client" (or none, /patient/:id) for a principal, anything else is a dependant.
    isDependant() {
      return !!this.$route.params.type && this.$route.params.type !== "client";
    },
    isLoading() {
      return this.loading > 0;
    },
    principal() {
      return this.isDependant ? this.client.user : null;
    },
    programme() {
      return this.client.sector || (this.principal && this.principal.sector) || "";
    },
    facilityName() {
      if (this.isDependant) return this.client.userprovider && this.client.userprovider.agency_name;
      return this.healthFacility && this.healthFacility.agency_name;
    },
    hmoName() {
      if (this.isDependant) return this.client.usertpa && this.client.usertpa.organization_name;
      return this.tpa && this.tpa.organization_name;
    },
    profileLink() {
      if (!this.client) return null;
      return this.isDependant ? `/dependent/${this.client.id}` : `/client/${this.client.id}`;
    },
    profileRows() {
      const c = this.client || {};
      const rows = [
        { label: "Date of birth", icon: "fe fe-calendar", value: c.dob ? `${moment(c.dob).format("D MMM YYYY")} (${moment().diff(c.dob, "years")} yrs)` : "" },
        { label: "Gender", icon: "fe fe-user", value: c.gender },
        { label: "Phone number", icon: "fe fe-phone", value: c.phone_number || (this.principal && this.principal.phone_number) },
        { label: "Programme", icon: "fe fe-layers", value: this.programme },
        { label: "Health facility", icon: "fe fe-home", value: this.facilityName },
        { label: "HMO / TPA", icon: "fe fe-briefcase", value: this.hmoName },
        { label: "Cover expires", icon: "fe fe-clock", value: c.expiry_date ? moment(c.expiry_date).format("D MMM YYYY") : "" },
      ];
      if (this.principal) {
        rows.unshift({
          label: "Principal",
          icon: "fe fe-users",
          value: `${this.principal.full_name || ""} (${this.principal.id_card_number || ""})`,
          to: `/patient/client/${this.principal.id}`,
        });
      }
      return rows.filter((r) => r.value);
    },
    claimsTotal() {
      return Number(this.claims.total) || this.claims.data.length;
    },
    claimsApproved() {
      return this.claims.data.reduce((sum, c) => sum + (Number(c.approved_total_cost) || 0), 0);
    },
    kpis() {
      const money = (n) => "₦" + Math.round(n || 0).toLocaleString("en-US");
      const kpis = [
        { label: "Encounters", icon: "fe fe-activity", tone: "bg-soft-primary text-primary", value: (this.records.meta.total || 0).toLocaleString("en-US") },
        { label: "Authorization codes", icon: "fe fe-send", tone: "bg-soft-info text-info", value: (this.referrals.meta.total || 0).toLocaleString("en-US") },
        { label: "Claims", icon: "fe fe-file-text", tone: "bg-soft-success text-success", value: this.claimsTotal.toLocaleString("en-US") },
      ];
      if (this.costs) {
        const all = this.costs.periods.all_time;
        kpis.push({
          label: "Cost of care (all time)", icon: "fe fe-trending-up", tone: "bg-soft-warning text-warning",
          value: money(all.total),
          hint: `Encounters ${money(all.encounter_cost)} · Claims ${money(all.approved)}`,
        });
      } else if (this.utilization) {
        kpis.push({
          label: "Utilization (all time)", icon: "fe fe-trending-up", tone: "bg-soft-warning text-warning",
          value: money(this.utilization.primary + this.utilization.secondary),
          hint: `Primary ${money(this.utilization.primary)} · Secondary ${money(this.utilization.secondary)}`,
        });
      } else {
        kpis.push({
          label: "Approved claims", icon: "fe fe-trending-up", tone: "bg-soft-warning text-warning",
          value: money(this.claimsApproved),
          hint: this.claimsTotal > this.claims.data.length ? `Latest ${this.claims.data.length} claims` : "",
        });
      }
      return kpis;
    },
  },
  beforeMount() {
    this.user = JSON.parse(localStorage.getItem("user"));
  },
  watch: {
    // Following the principal link reuses this component with new params.
    "$route.params"() {
      this.load();
    },
  },
  methods: {
    load() {
      this.client = "";
      this.utilization = null;
      this.costs = null;
      this.fetchUser();
      this.HRecord();
      this.getReferrals();
      this.getClaims();
    },
    track(promise) {
      this.loading++;
      return promise.finally(() => { this.loading--; });
    },
    claimStatus(claim) {
      return getClaimStatus(claim);
    },
    codeTone(status) {
      return { approved: "success", rejected: "danger", "in-progress": "info", pending: "warning" }[status] || "warning";
    },
    codeIcon(status) {
      return { approved: "fe fe-check", rejected: "fe fe-x", "in-progress": "fe fe-loader", pending: "fe fe-clock" }[status] || "fe fe-clock";
    },
    fetchUser() {
      const id = this.$route.params.id;
      if (!this.isDependant) {
        this.track(this.axios.get(`/api/v1/auth/user/zam/${id}`))
          .then((response) => {
            this.client = response.data.user;
            this.healthFacility = response.data.provider;
            this.tpa = response.data.tpa;
          })
          .catch((error) => console.error(error));
      } else {
        this.track(this.axios.get(`/api/v1/auth/getSpeicifDependant/${id}`))
          .then((response) => {
            this.client = response.data.data;
            this.utilization = {
              primary: Number(response.data.utilization_primary) || 0,
              secondary: Number(response.data.utilization_secondary) || 0,
            };
          })
          .catch((error) => console.error(error));
      }
    },
    HRecord() {
      const id = this.$route.params.id;
      this.track(this.axios.post(`/api/v1/auth/service_summary-agency`, {
        client_id: this.isDependant ? null : id,
        dependent_id: this.isDependant ? id : null,
        agency_id: agency.id,
        paginate_value: 30,
      }))
        .then((response) => { this.records = response.data; })
        .catch((error) => console.error(error));
    },
    getReferrals() {
      this.track(this.axios.get(`/api/v1/auth/referrals-patient/${this.$route.params.id}`, {
        params: { user_type: this.isDependant ? "Dependent" : "client" },
      }))
        .then((response) => { this.referrals = response.data; })
        .catch((error) => console.error(error));
    },
    getClaims() {
      const id = this.$route.params.id;
      this.track(this.axios.get(`/api/v1/auth/claims/${agency.id}`, {
        params: {
          client_id: this.isDependant ? null : id,
          dependent_id: this.isDependant ? id : null,
          paginate_value: CLAIMS_PER_PAGE,
        },
      }))
        .then((response) => { this.claims = response.data; })
        .catch((error) => console.error(error));
    },
  },
  created() {
    this.load();
  },
};
</script>

<style scoped>
.patient-avatar {
  width: 88px;
  height: 88px;
  background: #fff;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.25);
}

.form-card,
.table-card {
  border-radius: 14px;
  overflow: hidden;
}

.code-list {
  max-height: 460px;
  overflow-y: auto;
}

.code-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 0;
  border-bottom: 1px solid #f1f5f9;
}

.code-row:last-child { border-bottom: none; }
.code-row--muted { color: #475569; }

.code-dot {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 0.95rem;
}

.code-dot--success { background: #dcfce7; color: #15803d; }
.code-dot--danger  { background: #fee2e2; color: #b91c1c; }
.code-dot--info    { background: #dbeafe; color: #1d4ed8; }
.code-dot--warning { background: #fef3c7; color: #b45309; }

.pill {
  display: inline-flex;
  align-items: center;
  border-radius: 20px;
  padding: 3px 10px;
  font-size: 0.74rem;
  font-weight: 700;
  text-transform: capitalize;
  white-space: nowrap;
}

.pill--success   { background: #dcfce7; color: #15803d; }
.pill--danger    { background: #fee2e2; color: #b91c1c; }
.pill--info      { background: #dbeafe; color: #1d4ed8; }
.pill--warning   { background: #fef3c7; color: #b45309; }
.pill--indigo    { background: #e0e7ff; color: #4338ca; }
.pill--forwarded { background: #ede9fe; color: #6d28d9; }
.pill--dark      { background: #e2e8f0; color: #334155; }
.pill--draft     { background: #f1f5f9; color: #64748b; }

.empty-state {
  text-align: center;
  padding: 40px 16px !important;
  color: #94a3b8;
}

.empty-state i { font-size: 2rem; opacity: 0.5; }
.empty-state p { margin: 8px 0 0; }

.history-tabs { display: inline-flex; background: rgba(255, 255, 255, 0.15); border-radius: 10px; padding: 3px; gap: 2px; }
.history-tab { border: 0; background: transparent; color: rgba(255, 255, 255, 0.85); font-weight: 600; font-size: 0.8rem; padding: 5px 12px; border-radius: 8px; cursor: pointer; display: inline-flex; align-items: center; }
.history-tab--on { background: #fff; color: #1e3a5f; }
.history-tab:focus-visible { outline: 2px solid #fff; outline-offset: 1px; }
.history-count { margin-left: 6px; font-size: 0.7rem; background: rgba(15, 23, 42, 0.12); border-radius: 20px; padding: 0 7px; }
</style>
